import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { StatusPill } from '@/components/StatusPill';
import { Stat } from '@/components/Stat';
import { fmtDate, fmtUsd } from '@/lib/format';
import type { Me } from '@/types';

export function Home() {
  const api = useApi();
  const { data: me, isLoading, error } = useQuery({ queryKey: ['me'], queryFn: api.me });

  if (isLoading && !me) return <div className="placeholder">Loading...</div>;
  if (error || !me) return <div className="error-banner">Could not load your profile: {(error as Error)?.message}</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Welcome, {me.displayName.split(' ')[0]}</h1>
          <div className="sub">Key Travel's gateway to large language models &mdash; governed, budgeted and PII-safe.</div>
        </div>
        <StatusPill status={me.registrationStatus} />
      </div>

      {me.registrationStatus === 'NotRegistered' && <RequestAccessCard />}
      {me.registrationStatus === 'Pending' && <PendingCard isAdmin={me.isAdmin} />}
      {me.registrationStatus === 'Disabled' && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ color: 'var(--kt-red)', marginBottom: 8 }}>Access disabled</h3>
            <p style={{ color: 'var(--kt-grey-500)' }}>
              Your registration has been disabled. Contact an administrator if you believe this is a mistake.
            </p>
          </div>
        </div>
      )}
      {me.registrationStatus === 'Active' && <ActiveSummary me={me} />}
    </>
  );
}

function ActiveSummary({ me }: { me: Me }) {
  const budget = me.monthlyBudgetUsd;
  const spend = me.spendThisMonthUsd;
  const remaining = budget === null ? null : budget - spend;
  const remainingVariant =
    remaining !== null && remaining <= 0 ? 'alert'
    : budget !== null && remaining !== null && remaining < budget * 0.1 ? 'warn'
    : undefined;

  return (
    <>
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <Stat label="Monthly budget" value={budget === null ? 'Unlimited' : fmtUsd(budget)} />
        <Stat
          label="Spend this month"
          value={fmtUsd(spend)}
          sub={`Resets ${fmtDate(me.budgetResetsAtUtc)} (1st of the month, UTC)`}
        />
        <Stat
          label="Remaining"
          value={remaining === null ? 'Unlimited' : fmtUsd(Math.max(remaining, 0))}
          sub={remaining !== null && remaining <= 0 ? 'Budget exhausted' : undefined}
          variant={remainingVariant}
        />
      </div>

      <div className="card">
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ color: 'var(--kt-heading)', marginBottom: 4 }}>You're all set</h3>
            <p style={{ color: 'var(--kt-grey-500)' }}>
              Try models in the playground, or track what you've spent this month.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Link to="/playground" className="btn-primary">Open playground</Link>
            <Link to="/usage" className="btn-secondary">My usage</Link>
          </div>
        </div>
      </div>

      {budget !== null && <BudgetPreferencesCard me={me} />}
    </>
  );
}

/** Alert-threshold preference + "request more budget", shown only to budgeted callers. */
function BudgetPreferencesCard({ me }: { me: Me }) {
  const api = useApi();
  const qc = useQueryClient();
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestedBudget, setRequestedBudget] = useState('');
  const [justification, setJustification] = useState('');

  const saveThreshold = useMutation({
    mutationFn: (pct: number | null) => api.setAlertThreshold(pct),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] })
  });

  const requestBudget = useMutation({
    mutationFn: () =>
      api.requestBudgetIncrease(requestedBudget ? Number(requestedBudget) : null, justification.trim()),
    onSuccess: () => {
      setRequestOpen(false);
      setRequestedBudget('');
      setJustification('');
    }
  });

  return (
    <div className="card">
      <div className="card-header"><h3>Budget alerts &amp; requests</h3></div>
      <div className="card-body">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="alert-threshold">Email me when my spend reaches</label>
            <select
              id="alert-threshold"
              value={me.alertThresholdPercent ?? ''}
              disabled={saveThreshold.isPending}
              onChange={e => saveThreshold.mutate(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">Don't email me</option>
              {[50, 75, 90, 100].map(p => (
                <option key={p} value={p}>{p}% of budget</option>
              ))}
            </select>
          </div>
          <button className="btn-secondary" onClick={() => setRequestOpen(o => !o)}>
            Request more budget
          </button>
        </div>
        <p style={{ color: 'var(--kt-grey-500)', fontSize: '.82rem', marginTop: 8 }}>
          One alert email per month, sent to your registered address.
        </p>
        {saveThreshold.error && (
          <div className="error-banner">Could not save: {(saveThreshold.error as Error).message}</div>
        )}

        {requestOpen && (
          <div style={{ marginTop: 14, borderTop: '1px solid var(--kt-grey-200)', paddingTop: 14 }}>
            {requestBudget.isSuccess ? (
              <p style={{ color: requestBudget.data?.notificationSent ? 'var(--kt-heading)' : 'var(--kt-orange)' }}>
                {requestBudget.data?.notificationSent
                  ? 'Request sent — the administrators have been emailed and it\'s on their Approvals page.'
                  : 'Request recorded on the administrators\' Approvals page. The notification email could not be sent, so you may want to give them a nudge.'}
              </p>
            ) : (
              <>
                <div className="field">
                  <label htmlFor="requested-budget">Requested monthly budget (USD, optional)</label>
                  <input
                    id="requested-budget"
                    type="number"
                    min={1}
                    placeholder={`e.g. ${Math.ceil((me.monthlyBudgetUsd ?? 50) * 2)}`}
                    value={requestedBudget}
                    onChange={e => setRequestedBudget(e.target.value)}
                    style={{ maxWidth: 220 }}
                  />
                </div>
                <div className="field">
                  <label htmlFor="budget-justification">Why do you need more?</label>
                  <textarea
                    id="budget-justification"
                    rows={3}
                    placeholder="e.g. Monthly contract summarisation runs are hitting the cap mid-month"
                    value={justification}
                    onChange={e => setJustification(e.target.value)}
                  />
                </div>
                {requestBudget.error && (
                  <div className="error-banner">Could not send: {(requestBudget.error as Error).message}</div>
                )}
                <button
                  className="btn-primary"
                  disabled={requestBudget.isPending}
                  onClick={() => requestBudget.mutate()}
                >
                  {requestBudget.isPending ? 'Sending...' : 'Send request to admins'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PendingCard({ isAdmin }: { isAdmin: boolean }) {
  const api = useApi();
  const qc = useQueryClient();

  // Re-submitting the access request promotes gateway admins straight to Active
  // (the API auto-approves admins).
  const activate = useMutation({
    mutationFn: () => api.requestAccess(undefined),
    onSuccess: me => qc.setQueryData(['me'], me)
  });

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ color: 'var(--kt-heading)', marginBottom: 8 }}>Access request pending</h3>
        <p style={{ color: 'var(--kt-grey-500)' }}>
          Your request has been submitted and is waiting for an administrator to approve it.
          You will be able to use the playground and the API once your registration is active.
        </p>
        {isAdmin && (
          <div style={{ marginTop: 14 }}>
            {activate.error && (
              <div className="error-banner">Could not activate: {(activate.error as Error).message}</div>
            )}
            <button className="btn-primary" disabled={activate.isPending} onClick={() => activate.mutate()}>
              {activate.isPending ? 'Activating...' : 'Activate my access (admin)'}
            </button>
            <p style={{ color: 'var(--kt-grey-500)', fontSize: '.82rem', marginTop: 8 }}>
              You're a gateway administrator, so your registration is approved automatically.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function RequestAccessCard() {
  const api = useApi();
  const qc = useQueryClient();
  const [justification, setJustification] = useState('');

  const request = useMutation({
    mutationFn: () => api.requestAccess(justification.trim() || undefined),
    onSuccess: me => qc.setQueryData(['me'], me)
  });

  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <div className="card-header"><h3>Request access</h3></div>
      <div className="card-body">
        <p style={{ color: 'var(--kt-grey-500)', marginBottom: 14 }}>
          You're not registered for the AI gateway yet. Tell us what you'd like to use it for and an
          administrator will review your request.
        </p>
        {request.error && (
          <div className="error-banner">Could not submit request: {(request.error as Error).message}</div>
        )}
        <div className="field">
          <label htmlFor="justification">Justification (optional)</label>
          <textarea
            id="justification"
            rows={4}
            placeholder="e.g. Summarising supplier contracts for the ops team"
            value={justification}
            onChange={e => setJustification(e.target.value)}
          />
        </div>
        <button className="btn-primary" onClick={() => request.mutate()} disabled={request.isPending}>
          {request.isPending ? 'Submitting...' : 'Request access'}
        </button>
      </div>
    </div>
  );
}
